import type { PartnerConfig } from "@expert/app-config/types";
import { ControlPanelContainer, type ServiceProvider, useIframeControls } from "@expert/service-providers";
import { GlobalError, WorkspaceLoader } from "@/common-ui";
import KonamiCode from "@/components/KonamiCode";
import type { CreateSdkParams } from "@/utils/createAgentSdk";
import { AgentSdkContext } from "./AgentSdkContext";
import type { AgentSdk } from "./agentSdk";
import type { AgentSdkBase } from "./agentSdkBase";
import { type AgentSdkLoaderProps, useAgentSdkLoader } from "./hooks/useAgentSdkLoader";
import { EvlDisconnectedErrorPage } from "./voice/EvlDisconnectedErrorPage";

export type AgentSdkProviderCreator = (params: CreateSdkParams) => Promise<AgentSdk>;

interface AgentSdkProviderProps {
    employeeNum: string | null;
    userFriendlyName?: string;
    children?: React.ReactNode;
    serviceProviders: readonly ServiceProvider[];
    partnerConfig?: PartnerConfig;
    additionalSdks?: AgentSdkBase[];
    onAuthRedirect?: VoidFunction;
}

type InnerProviderProps = AgentSdkProviderProps & Pick<AgentSdkLoaderProps, "createContainer">;

/** Inner provider so that containers can be rendered before SDK loading. */
function InnerProvider({
    employeeNum,
    children,
    userFriendlyName,
    serviceProviders,
    partnerConfig,
    createContainer,
    additionalSdks,
    onAuthRedirect,
}: InnerProviderProps) {
    const { sdk, loadingMessage, errorMessage, failureType } = useAgentSdkLoader({
        employeeNum,
        userFriendlyName,
        serviceProviders,
        createContainer,
        partnerConfig,
        additionalSdks,
        onAuthRedirect,
    });

    switch (failureType) {
        case "none":
            break;
        case "evl-failed":
            return <EvlDisconnectedErrorPage />;
        case "general":
            return <GlobalError title="Failed to create Agent SDK" msg={errorMessage} />;
    }

    if (loadingMessage) {
        return <WorkspaceLoader msg={loadingMessage} />;
    }

    if (!sdk) {
        throw new Error("Failed to initialize AgentSDK.");
    }

    return <AgentSdkContext.Provider value={sdk}>{children}</AgentSdkContext.Provider>;
}

export function AgentSdkProvider(props: AgentSdkProviderProps) {
    const { controlPanelResolversByName, showControlPanel, setShowControlPanel, createContainer } = useIframeControls();

    return (
        <>
            {Array.from(controlPanelResolversByName, ([name, resolve]) => (
                <ControlPanelContainer
                    key={name}
                    name={name}
                    opened={showControlPanel}
                    onClose={() => setShowControlPanel(false)}
                    ref={(element) => {
                        if (element) resolve(element);
                    }}
                />
            ))}
            <KonamiCode callback={() => setShowControlPanel(true)} />
            <InnerProvider {...props} createContainer={createContainer} />
        </>
    );
}
